import React, { useState, useEffect } from 'react';
const title = 'Chart of Accounts';
const description = `Your Chart of Accounts is like a master
 list of all the financial categories in your business. It
  includes standard groups such as Income, Expenses, Assets,
   Liabilities, and Equity, each with its own set of accounts
    to help you keep track of money coming in and out. If your
     business has unique needs, you can add new accounts to this
      list. If you're not sure which accounts to use, it's a
       good idea to talk to your accountant.`;

const chartOfAccounts_images = [
    '/images/accountant/chart-of-accounts/all-account.png', // 0
    '/images/accountant/chart-of-accounts/data-sorting.png', // 1
    '/images/accountant/chart-of-accounts/export-item.png', // 2
    '/images/accountant/chart-of-accounts/import.png', // 3
    '/images/accountant/chart-of-accounts/item-options.png', // 4
    '/images/accountant/chart-of-accounts/new-account.png', // 5
    '/images/accountant/chart-of-accounts/search-account.png', // 6
    '/images/accountant/chart-of-accounts/view-account.png', // 7
    '/images/accountant/chart-of-accounts/chart-of-account.png', // 8
];

const topic = [
    {
        title: 'Home',
        description: ``,
        id: 'home',
    },
    {
        title: '5 Main Accounts',
        description: `.`,
        id: 'five-main-accounts',
    },
    {
        title: 'Available Actions',
        description: ``,
        id: 'available-actions',
    },
    {
        title: 'New Account',
        description: ``,
        id: 'new-account',
    },
    {
        title: 'View Details',
        description: ``,
        id: 'view-details',
    },
    {
        title: 'Import & Export',
        description: ``,
        id: 'import-export',
    },
    {
        title: 'Sorting, Search and Controls',
        description: ``,
        id: 'search-sorting-controls',
    },
];

const available_action = [
    {
        strong: 'New Account',
        description: ' With our search option, you can easily find any report or financial data you need, all in one place.'
    },
    {
        strong: 'View Details',
        description: `
            To get a closer look at a report, you can open it in a new tab. This lets you view it on its own, making it easier to see all the details clearly.`
    },
    {
        strong: 'Export',
        description: `
            With the Export button, you can download the data to share it with anyone you want.`
    },
    {
        strong: 'Import',
        description: ' To leave this page, just click the red \'X\' button.'
    },
    {
        strong: 'Sorting',
        description: ' You can filter your view by month or by branch to see exactly the data you need.'
    },
    {
        strong: 'Search',
        description: ' Our Compare Periods feature lets you see how your business is performing over different timeframes, so you can easily spot trends and make smart decisions.'
    },
    {
        strong: 'Controls',
        description: ' Our Compare Periods feature lets you see how your business is performing over different timeframes, so you can easily spot trends and make smart decisions.'
    },

];

const five_account = [
    {
        title: 'Asset Account',
        description: `
        Assets are everything your business owns
         that has value. This includes the money
          in your bank accounts (Cash and Bank),
           items you own for a long time like 
           buildings or equipment (Fixed Asset),
            and anything you've sold but haven't
             been paid for yet (Accounts Receivable).
              It also includes your product inventory
               (Stock), plus other items like prepayments
                or future tax refunds.
        `,
        id: 'asset-account'
    },
    {
        title: 'Liability Account',
        description: `Liabilities are the exact
         opposite—they are everything your 
         business owes to others. This covers 
         what you need to pay back to suppliers
          (Accounts Payable), outstanding credit
           card balances, and any long-term debts
            like loans (Long Term Liability). 
            It also includes taxes you have to 
            pay and other short-term debts.`,
        id: 'liability-account'
    },
    {
        title: 'Equity Account',
        description: `Equity represents the owner's
         stake in the business. Think of it as the
          value of the company that belongs to the
           owners after you've paid off all of the
            liabilities. This value changes over time
             based on the business's profits or losses,
              and any money the owner puts in or takes out.`,
        id: 'equity-account'
    },
    {
        title: 'Income Account',
        description: `
        Income is all the money your business
         earns from selling products or services.
          This is the top line of your business’s
           financial performance. It also includes
            any other earnings from non-core activities,
             like interest from a bank account.
        `,
        id: 'income-account'
    },
    {
        title: 'Expense Account',
        description: `
        Expenses are all the costs your business
         pays to operate. This covers everything
          from rent and salaries to the cost of 
          materials and other day-to-day costs. 
          Tracking your expenses is crucial because
           when you subtract them from your income,
            you can see if your business is making
             a profit or a loss.
        `,
        id: 'expenses-account'
    },
]

const ChartOfAccounts: React.FC = () => {
    const [activeId, setActiveId] = useState(topic[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const newActiveId = entry.target.id;
                        setActiveId(newActiveId);
                        window.history.replaceState(null, '', `#${newActiveId}`);
                    }
                });
            },
            {
                root: null,
                rootMargin: '-50% 0% -50% 0%',
                threshold: 0,
            }
        );

        topic.forEach((item) => {
            const section = document.getElementById(item.id);
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    const handleItemClick = (id: string) => {
        setActiveId(id);
    };

    return (
        <div className="max-w-4xl min-h-screen bg-gray-50 flex gap-2">
            <div className="overflow-auto mx-auto flex-2">

                {/* Chart of Accounts */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold pt-4 text-gray-900">
                        {title}
                    </h1>
                    <p className="text-gray-600 mt-4">
                        {description}
                    </p>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-6">
                        <img
                            src={chartOfAccounts_images[8]}
                            alt={title}
                            className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    </div>
                </div>

            {/*  five Accounts */}
                <div className="mb-6">

                    <h1 className="text-2xl font-bold pt-4 text-gray-900" id={topic[1].id}>
                        {topic[1].title}
                    </h1>

                    {
                        five_account.map((item,index)  => (
                            <>
                                <h1 className="text-xl font-bold pt-4 text-gray-900">
                                    {item.title}
                                </h1>
                                <p className="text-gray-600 mt-2">
                                    {item.description}
                                </p>
                            </>
                        ))
                    }
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-6">
                        <p className="text-gray-600 mt-4">
                            {description}
                        </p>
                        <img
                            src={chartOfAccounts_images[0]}
                            alt={title}
                            className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    </div>
                </div>

            {/* Available action */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"  id={topic[2].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Actions</h2>
                    <p className="text-gray-600 mb-4">
                        Chart of Accounts Page offers several action buttons in the top toolbar :
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                        {
                            available_action.map((item,index)=>(
                                <li key={index}><strong>{item.strong}</strong></li>
                            ))
                        }
                    </ul>

                </div>
            </div>

            {/* side route */}
            <div className="fixed w-1/5 right-2">
                <ul>
                    {topic.map((item) => (
                        <li key={item.id} className="mb-4">
                              <span className="text-xs">
                                    <a
                                        className={`hover:text-blue-700 border-l p-4 ${
                                            activeId === item.id ? 'text-blue-700 font-bold border-blue-700' : ''
                                        }`}
                                        href={`#${item.id}`}
                                        onClick={() => handleItemClick(item.id)}
                                    >
                                      {item.title}
                                    </a>
                              </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default ChartOfAccounts;
